import {
  Box,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material'
import { MouseEvent, useCallback, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { GridProgress, TableNoDataRow } from '@/common/components'
import CustomPagination from '@/common/components/CustomPagination/CustomPagination'
import { usePagination } from '@/common/components/CustomPagination/hooks/usePagination'
import { useSearchTenantHierarchies } from '@/features/tenant/hooks/useSearchTenantHierarchies'
import { useTenantSearchFormContext } from '@/features/tenant/hooks/useTenantSearchFormContext'
import { TenantHierarchyFilter } from '@/services/api/tenant/subsidiaries'

import { TenantHierarchyTableRow } from './TenantHierarchyTableRow'
import {
  COMPANY_COLUMN_WIDTH,
  filterBarStyles,
  filterToggleGroupStyles,
  headerCellStyles,
  paginationStackStyles,
  setupHeaderCellStyles,
  tableContainerStyles,
  tableHeadStyles,
  tableStyles,
  tableWrapperStyles,
} from './TenantHierarchyTableStyles'

export const TenantHierarchyTable = (): JSX.Element => {
  const { t, i18n } = useTranslation('general')
  const { page, size: limit, handlePageChange, handleLimitChange, resetPage } = usePagination()
  const { values, setFieldValue } = useTenantSearchFormContext()
  const searchQuery = (values?.name || '').trim() || undefined
  const filterType: TenantHierarchyFilter = values?.type || 'all'
  const { data: list = { items: [], total: 0 }, isFetching, isUninitialized } = useSearchTenantHierarchies(page, limit)
  const isLoading = isFetching || isUninitialized

  const prefetchNext = useCallback(() => {}, [])

  const handleFilterChange = useCallback(
    (event: MouseEvent<HTMLElement>, newFilter: TenantHierarchyFilter | null) => {
      if (newFilter !== null) {
        setFieldValue('type', newFilter)
      }
    },
    [setFieldValue],
  )

  useEffect(() => {
    resetPage()
  }, [filterType, searchQuery, resetPage])

  return useMemo(
    () => (
      <GridProgress loading={isLoading} key={i18n.language}>
        <Stack direction="row" sx={filterBarStyles}>
          <ToggleButtonGroup
            exclusive
            value={filterType}
            onChange={handleFilterChange}
            aria-label={t('tenant_filter_by_setup_type')}
            sx={filterToggleGroupStyles}>
            <ToggleButton value="all">{t('tenant_filter_all')}</ToggleButton>
            <ToggleButton value="standalone">{t('tenant_type_standalone')}</ToggleButton>
            <ToggleButton value="parent">{t('tenant_type_parent')}</ToggleButton>
            <ToggleButton value="child">{t('tenant_table_subsidiaries')}</ToggleButton>
          </ToggleButtonGroup>
        </Stack>
        <Box sx={tableWrapperStyles}>
          <TableContainer sx={tableContainerStyles}>
            <Table sx={tableStyles}>
              <colgroup>
                <col style={{ width: COMPANY_COLUMN_WIDTH }} />
                <col />
              </colgroup>
              <TableHead sx={tableHeadStyles}>
                <TableRow>
                  <TableCell sx={headerCellStyles}>{t('company')}</TableCell>
                  <TableCell sx={setupHeaderCellStyles}>{t('tenant_table_setup_type')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {list.items.length === 0 ? (
                  <TableNoDataRow colSpan={2} message={t('noTenantsFound', { defaultValue: 'No tenants found' })} />
                ) : (
                  list.items.map((item) => (
                    <TenantHierarchyTableRow key={item.tenant.id} item={item} searchQuery={searchQuery} />
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {list.total > 25 && (
          <Stack
            direction="row"
            sx={[
              {
                alignItems: 'center',
              },
              ...(Array.isArray(paginationStackStyles) ? paginationStackStyles : [paginationStackStyles]),
            ]}>
            <CustomPagination
              total={list.total}
              limit={limit}
              page={page}
              onPageChange={handlePageChange}
              onLimitChange={handleLimitChange}
              handlePrefetch={prefetchNext}
            />
          </Stack>
        )}
      </GridProgress>
    ),
    [
      isLoading,
      list,
      page,
      limit,
      i18n.language,
      searchQuery,
      filterType,
      handleFilterChange,
      prefetchNext,
      handlePageChange,
      handleLimitChange,
      t,
    ],
  )
}
